import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, FileText, ExternalLink, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BACKEND_URL } from '../../lib/site';

const CATEGORIES = [
  'Fire Resistance', 'EPS Density', 'Fire Retardancy', 'Thermal Conductivity',
  'Compressive Strength', 'Tensile Strength', 'Mesh Coating', 'Installation Methodology',
  'Structural Documentation', 'General',
];

export const DocumentsTab = ({ authAxios }) => {
  const [documents, setDocuments] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authAxios.get('/documents');
      setDocuments(res.data.documents || []);
    } catch (e) {
      toast.error('Failed to load documents');
      setDocuments([]);
    }
  }, [authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    if (!title.trim() || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('category', category);
      fd.append('file', file);
      await authAxios.post('/admin/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document published — visible in the Technical section');
      setTitle('');
      setFile(null);
      document.getElementById('doc-file-input').value = '';
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await authAxios.delete(`/admin/documents/${id}`);
      toast.success('Document removed');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Publish Technical Document</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Upload verified test certificates and documentation (PDF or image, max 15 MB). Published documents appear in the
          “Engineered. Tested. Documented.” section for visitors.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. EPS Density Test Report" className="h-10 mt-1.5" data-testid="admin-doc-title-input" />
          </div>
          <div>
            <label className="text-sm font-medium">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-10 mt-1.5" data-testid="admin-doc-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-sm font-medium">File (PDF / image)</label>
            <Input
              id="doc-file-input"
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="h-10 mt-1.5 pt-1.5"
              data-testid="admin-doc-file-input"
            />
          </div>
        </div>
        <Button className="h-10 mt-5" disabled={!title.trim() || !file || uploading} onClick={upload} data-testid="admin-doc-upload-button">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
          Publish Document
        </Button>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Published Documents</h3>
        {documents === null ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : documents.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="admin-docs-empty-state">
            No documents published yet. Visitors currently see “Available on request” placeholders.
          </p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {documents.map((d) => (
              <div key={d.id} className="py-3.5 flex items-center gap-4" data-testid={`admin-doc-row-${d.id}`}>
                <FileText className="w-4.5 h-4.5 text-[#155E6D] shrink-0" size={18} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{d.title}</p>
                  <p className="text-xs text-muted-foreground">{d.category} · {d.filename}</p>
                </div>
                <a
                  href={`${BACKEND_URL}${d.file_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 rounded-md hover:bg-muted text-muted-foreground"
                  aria-label={`View ${d.title}`}
                  data-testid={`admin-doc-view-${d.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 text-destructive"
                  aria-label={`Delete ${d.title}`}
                  data-testid={`admin-doc-delete-${d.id}`}
                  onClick={() => remove(d.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
